/**
 * One place that turns access failures from any API call into events the
 * Dashboard page listens to, so a revoke that lands while a tab is open shows
 * the "no access" screen instead of a broken table — without touching the
 * ~120 bare fetch calls in api.service.ts.
 *
 * 403 with `no_tab_grant`  -> "dashboard:no-access"  { tab? }
 * 403 with `not_dashboard_account` -> "dashboard:not-dashboard-account"
 * 503 with `access_check_failed` -> "dashboard:access-check-failed"
 */

export const NO_ACCESS_EVENT = "dashboard:no-access";
export const NOT_DASHBOARD_ACCOUNT_EVENT = "dashboard:not-dashboard-account";
export const ACCESS_CHECK_FAILED_EVENT = "dashboard:access-check-failed";
/** The session behind the open page is gone: signed out elsewhere, or demoted and revoked. */
export const SESSION_ENDED_EVENT = "dashboard:session-ended";

export type NoAccessDetail = { tab?: string; tabs?: string[]; path: string };

/** The body an access guard returns. Anything else is not a guard answer. */
export type GuardBody = { detail?: unknown; tab?: unknown; tabs?: unknown };

export type AccessEvent =
  | { name: typeof NO_ACCESS_EVENT; detail: NoAccessDetail }
  | { name: typeof NOT_DASHBOARD_ACCOUNT_EVENT; detail: { path: string } }
  | { name: typeof ACCESS_CHECK_FAILED_EVENT; detail: { path: string } }
  | { name: typeof SESSION_ENDED_EVENT; detail: { path: string } };

/**
 * Map one guard answer to the event it raises, or null when the response is
 * not a guard deny. Pure, so the mapping is testable on its own.
 */
export const classifyAccessResponse = (
  status: number,
  body: GuardBody | null,
  path: string,
): AccessEvent | null => {
  const detail = typeof body?.detail === "string" ? body.detail : undefined;
  if (status === 403 && detail === "no_tab_grant") {
    const tab = typeof body?.tab === "string" ? body.tab : undefined;
    const tabs = Array.isArray(body?.tabs)
      ? body.tabs.filter((t): t is string => typeof t === "string")
      : undefined;
    return { name: NO_ACCESS_EVENT, detail: { tab, tabs, path } };
  }
  if (status === 403 && detail === "not_dashboard_account") {
    return { name: NOT_DASHBOARD_ACCOUNT_EVENT, detail: { path } };
  }
  if (status === 503 && detail === "access_check_failed") {
    return { name: ACCESS_CHECK_FAILED_EVENT, detail: { path } };
  }
  // A demotion revokes every session of the account, so the page's next call
  // answers 401 rather than 403. Without this the page stayed on screen while
  // every request failed.
  if (status === 401 && detail === "Unauthorized") {
    return { name: SESSION_ENDED_EVENT, detail: { path } };
  }
  return null;
};

const isDashboardApi = (input: RequestInfo | URL): string | null => {
  const url =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname.startsWith("/api/") ? parsed.pathname : null;
  } catch {
    return null;
  }
};

let installed = false;

export const installAccessInterceptor = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const original = window.fetch.bind(window);
  const intercepted = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await original(input, init);
    const path = isDashboardApi(input);
    if (!path || (response.status !== 401 && response.status !== 403 && response.status !== 503)) {
      return response;
    }
    // Peek at the body without consuming the caller's copy.
    let body: GuardBody | null = null;
    try {
      body = (await response.clone().json()) as GuardBody;
    } catch {
      // Non-JSON 401/403/503 bodies are not access-guard responses.
    }
    const event = classifyAccessResponse(response.status, body, path);
    if (event) {
      window.dispatchEvent(new CustomEvent(event.name, { detail: event.detail }));
    }
    return response;
  };
  // Bun types `fetch` with extra static members; the browser only calls it.
  window.fetch = intercepted as unknown as typeof window.fetch;
};
